import { put, takeLatest, call } from "redux-saga/effects";
import {
  changePasswordFailure,
  changePasswordSuccess,
  CHANGE_PASSWORD_REQUEST,
  recoverPasswordFailure,
  recoverPasswordSuccess,
  RECOVER_PASSWORD_REQUEST,
  SIGN_IN_PROVIDER_REQUEST,
  SIGN_IN_REQUEST,
} from "../actions/signin";
import { setTokens } from "../actions/token";
import { signInSuccess, signInFailure } from "../actions/signin";
import {
  changePasswordService,
  recoverPasswordService,
  signInProviderService,
  signInService,
} from "../services/signIn";
import { setUserLogin } from "../../home/actions/loggedUser";
import { showGlobalError } from "../../app/actions/app";
import {
  PERMISSION_ENTITIES,
  usersAuthorizedToEnterPlatform,
} from "../../app/helpers/userAuth";
import { setPermissions } from "../actions/permissions";
import Cookies from "js-cookie";
import { api, environment, omuniWeb } from "../../app/conf";
import { generateDuration, getDomain, extractMinimalAuthorities } from "../helpers";
import { getCompanyConfigService } from "../../settings/services/preferences";
import { getPreferencesSuccess } from "../../settings/actions/preferences";

const CASES_KEY = "CASES";

const TICKETS_KEY = "TICKETS";

const DEFAULT_INITIAL_ROUTE = "/tickets";

const DEFAULT_PERMISSION = {};

const EMPTY_STRING = "";

const signInWorker = function* (action) {
  const { username, password, tokenId, history, recaptcha } = action.payload;
  try {
    let accessToken;
    let refreshToken;
    let user;

    //STEP 1: Sign in with provider
    if (tokenId) {
      const response = yield call(signInProviderService, tokenId);
      accessToken = response.data.access;
      refreshToken = response.data.refresh;
      user = response.data.user;
    }

    if (!tokenId) {
      const response = yield call(
        signInService,
        username,
        password,
        recaptcha?.token
      );

      accessToken = response.data.access;
      refreshToken = response.data.refresh;
      user = response.data.user;
    }

    const profileCode = "administrator";

    if (!usersAuthorizedToEnterPlatform.includes(profileCode)) {
      throw new Error();
    }

    yield put(
      setUserLogin({
        user: { ...user },
        permisionEntities: PERMISSION_ENTITIES,
        permission: DEFAULT_PERMISSION,
      })
    );
 

    const session = JSON.stringify({
      accessToken,
      refreshToken,
      authorities: extractMinimalAuthorities(user.permissions),
    });

    const options = {
      expires: generateDuration(),
      domain: getDomain(),
    };

    Cookies.set("session", session, options);

    // Fetch company config BEFORE setting tokens to avoid showing the dashboard flash.
    // The result is stored in Redux so PreferencesContext doesn't need a second request.
    let companyConfig = null;
    try {
      const configResponse = yield call(getCompanyConfigService, accessToken);
      companyConfig = configResponse.data;
      yield put(getPreferencesSuccess(companyConfig));
      const storedHome = companyConfig?.preferred_home?.toLowerCase() || '';
      if (storedHome) {
        localStorage.setItem('omuni_preferred_home', storedHome);
      } else {
        localStorage.removeItem('omuni_preferred_home');
      }
    } catch (_err) {
      // Config failure shouldn't block login
    }

    // setTokens is dispatched while signin.fetching is still true (signInSuccess not yet called).
    // LoggedUser skips its redirect to HOME while fetching=true, avoiding the dashboard flash.
    yield put(setTokens(accessToken, refreshToken, tokenId ? true : false));
    yield put(setPermissions(user.permissions));

    const preferredHome = companyConfig?.preferred_home?.toLowerCase() || EMPTY_STRING ;

    const caseModule = companyConfig?.module_configuration?.find(
      (module) => module.key === CASES_KEY
    );

    const ticketModule = companyConfig?.module_configuration?.find(
      (module) => module.key === TICKETS_KEY
    );

    if (!tokenId && recaptcha.token) {
      recaptcha.cleanupScript();
    }

    if (user.changePassword?.changePasswordRequired) {
      window.location.replace(`${omuniWeb}/change-password`);
      return;
    }

    if (preferredHome && preferredHome !== EMPTY_STRING ) {
      const target = preferredHome === 'dashboard'
        ? '/old/dashboard'
        : `${omuniWeb}/${preferredHome}`;
      window.location.replace(target);

      setTimeout(() => {
        window.history.pushState(null, "", target);
      }, 0);
      return;
    }

    if (ticketModule?.enabled ) {
      window.location.replace(`${omuniWeb}/tickets`);

      setTimeout(() => {
        window.history.pushState(null, "", `${omuniWeb}/tickets`);
      }, 0);
      return;
    }

    if (caseModule?.enabled) {
      window.location.replace(`/tickets`);

      setTimeout(() => {
        window.history.pushState(null, "", `/tickets`);
      }, 0);
      return;
    }

    history.push(DEFAULT_INITIAL_ROUTE);
    yield put(signInSuccess());
  } catch (err) {
    yield put(signInFailure());
    const status = err?.response?.status;
    if (status === 500) {
      yield put(showGlobalError(true, 500));
    } else {
      if (status === 404) {
        if (tokenId) {
          //this happens when you try to login and there's not a user in omuni which email is
          //the email of the account you're trying to login with
          yield put(showGlobalError(true, 409, "account_not_exists"));
        } else {
          //omuni login with a user that doesnt exists
          yield put(showGlobalError(true, 409, "email_not_exists"));
        }
      }
      if (status === 401) {
        yield put(showGlobalError(true, 409, "wrong_password"));
      }
      if (tokenId) {
        //we are in redirect page, and need to go back to login
        history.push("/");
      }
    }
  }
};

const recoverPasswordWorker = function* ({ payload: { email, url } }) {
  try {
    yield call(recoverPasswordService, email, url);
    yield put(recoverPasswordSuccess());
  } catch (err) {
    console.log(err);
    yield put(recoverPasswordFailure());
    yield put(showGlobalError(true, err));
  }
};

const changePasswordWorker = function* ({
  payload: { email, code, password },
}) {
  try {
    yield call(changePasswordService, email, code, password);
    yield put(changePasswordSuccess());
  } catch (err) {
    console.log(err);
    yield put(changePasswordFailure());
    yield put(showGlobalError(true, err));
  }
};

export default [
  takeLatest(SIGN_IN_REQUEST, signInWorker),
  takeLatest(SIGN_IN_PROVIDER_REQUEST, signInWorker),
  takeLatest(RECOVER_PASSWORD_REQUEST, recoverPasswordWorker),
  takeLatest(CHANGE_PASSWORD_REQUEST, changePasswordWorker),
];
